import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Columns3 } from "lucide-react";

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function buildPageList(current, totalPages) {
  // Returns an array of page numbers and "..." separators
  const pages = new Set([1, totalPages, current, current - 1, current + 1, current - 2, current + 2]);
  const arr = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push("…");
  }
  return out;
}

export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(total, page * pageSize);
  const pages = buildPageList(page, totalPages);

  return (
    <div data-testid="pagination" className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 text-sm">
      <div className="flex items-center gap-3">
        <span className="text-[#525252]">Rows per page:</span>
        <select data-testid="page-size-select" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="border border-black/10 rounded-sm px-2 py-1 bg-white">
          {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-[#525252]" data-testid="page-range">{startIdx}–{endIdx} of {total}</span>
      </div>
      <div className="flex items-center gap-1" data-testid="page-list">
        <button data-testid="page-prev" onClick={() => onPageChange(page - 1)} disabled={page <= 1}
                className="p-2 border border-black/10 rounded-sm disabled:opacity-40 hover:bg-black/5"
                aria-label="Previous page"><ChevronLeft size={16}/></button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`gap-${i}`} className="px-2 text-[#525252]">…</span>
          ) : (
            <button
              key={p}
              data-testid={`page-num-${p}`}
              onClick={() => onPageChange(p)}
              className={`min-w-[36px] px-2 py-1 border rounded-sm transition-colors ${
                p === page
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white border-black/10 hover:bg-black/5"
              }`}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button data-testid="page-next" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
                className="p-2 border border-black/10 rounded-sm disabled:opacity-40 hover:bg-black/5"
                aria-label="Next page"><ChevronRight size={16}/></button>
      </div>
    </div>
  );
}

export function SortableHeader({ label, sortKey, currentSort, currentDir, onSort, className = "" }) {
  const active = currentSort === sortKey;
  const dir = active ? currentDir : null;
  return (
    <button
      data-testid={`sort-${sortKey}`}
      onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 text-left text-xs uppercase tracking-[0.15em] text-[#525252] hover:text-[#0A0A0A] transition-colors ${className}`}
    >
      {label}
      <span className="font-mono text-[10px] w-3 inline-block">
        {sortArrow(dir)}
      </span>
    </button>
  );
}

function sortArrow(dir) {
  if (dir === "asc") return "▲";
  if (dir === "desc") return "▼";
  return "";
}

export function MultiSelectStatusFilter({ options, selected, onToggle, testIdPrefix = "filter" }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-chips`}>
      {options.map((opt) => {
        const active = selected.has(opt);
        return (
          <button
            key={opt}
            data-testid={`${testIdPrefix}-${opt}`}
            onClick={() => onToggle(opt)}
            className={`px-4 py-2 text-sm rounded-sm border capitalize transition-colors ${
              active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-black/10 hover:border-black/30"
            }`}
            aria-pressed={active}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function MultiSelectDropdown({ label, options, selected, onToggle, onClear, testId = "multi-dropdown" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const count = selected.size;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 text-sm border px-3 py-2 rounded-sm transition-colors ${
          count > 0 ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10 hover:border-black/30"
        }`}
      >
        {label}
        {count > 0 && (
          <span className={`text-xs px-1.5 py-0.5 rounded-sm ${count > 0 ? "bg-white/20 text-white" : "bg-black/5"}`}>{count}</span>
        )}
      </button>
      {open && (
        <div data-testid={`${testId}-menu`} className="absolute right-0 mt-1 z-30 bg-white border border-black/10 rounded-sm shadow-lg p-2 w-72 max-h-80 overflow-y-auto">
          <div className="flex items-center justify-between px-2 py-1">
            <div className="text-xs uppercase tracking-wider text-[#525252]">{label}</div>
            {count > 0 && onClear && (
              <button type="button" data-testid={`${testId}-clear`} onClick={onClear} className="text-xs text-[var(--brand-primary)] hover:underline">Clear</button>
            )}
          </div>
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-black/5 cursor-pointer text-sm">
              <input
                type="checkbox"
                data-testid={`${testId}-${opt}`}
                checked={selected.has(opt)}
                onChange={() => onToggle(opt)}
              />
              <span className="truncate" title={opt}>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function ColumnVisibility({ columns, hidden, onToggle, testId = "column-vis" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 text-sm border border-black/10 bg-white px-3 py-2 rounded-sm hover:border-black/30"
      >
        <Columns3 size={14}/> Columns
      </button>
      {open && (
        <div data-testid={`${testId}-menu`} className="absolute right-0 mt-1 z-30 bg-white border border-black/10 rounded-sm shadow-lg p-2 w-56">
          <div className="text-xs uppercase tracking-wider text-[#525252] px-2 py-1">Show columns</div>
          {columns.map((c) => (
            <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-black/5 cursor-pointer text-sm">
              <input
                type="checkbox"
                data-testid={`${testId}-${c.key}`}
                checked={!hidden.has(c.key)}
                onChange={() => onToggle(c.key)}
                disabled={c.required}
              />
              <span className={c.required ? "text-[#999]" : ""}>{c.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function compareValues(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  let cmp;
  if (typeof a === "number" && typeof b === "number") cmp = a - b;
  else cmp = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
  return dir === "desc" ? -cmp : cmp;
}
